import React, { type FC, useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import type { NotificationType } from "../../../../../../../common-deprecated/types/CommonTypes";
import styles from "./NotificationContainer.module.scss";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import { scrollToTarget } from "../../../../../../../common-deprecated/utils/scrollUtils";
import { useDebouncedEffect } from "../../../../../../../common-deprecated/hooks";

const Notification: FC<{
    notification: NotificationType;
    index: number;
    onTransitionEnd: (index: number) => void;
}> = ({ notification, onTransitionEnd, index }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const interval = useRef<NodeJS.Timer>();
    const [open, setOpen] = useState<boolean>(false);
    const [timer, setTimer] = useState<number>(0);
    const [userIsFocused, setUserIsFocused] = useState(false);
    const totalTimeVisible = 8000; // 8seconds is the standard amount for a timer when it comes to accesible notifications

    const closeNotification = useCallback(() => {
        setOpen(false);
    }, []);

    useDebouncedEffect(
        () => {
            if (timer >= totalTimeVisible && interval.current) {
                clearInterval(interval.current);
                closeNotification();
            }
        },
        500,
        [timer, interval],
    );

    useEffect(() => {
        setOpen(true);

        interval.current = setInterval(() => {
            if (!userIsFocused) {
                setTimer(timer + 1000);
            }
        }, 1000);

        return () => clearInterval(interval.current);
    }, [userIsFocused, timer, interval]);

    return (
        <div
            // Every notification needs to have a seperate margin value.
            key={notification.displaySettings?.notificationIndex ?? index}
            ref={ref}
            onAnimationEnd={() => onTransitionEnd(notification.displaySettings?.notificationIndex ?? index)}
            onMouseOver={() => setUserIsFocused(true)}
            onMouseOut={() => setUserIsFocused(false)}
            className={clsx(styles.notification, open && styles.open)}
        >
            <div className={styles["title-wrapper"]}>
                {notification.title}
                {notification.action?.closeButton && (
                    <Button variant="clean" aria-label="close" onClick={closeNotification}>
                        <Icon variant="close" sizing="md" aria-hidden="true" color="primary-white" />
                    </Button>
                )}
            </div>
            {notification.description}
            {notification.action?.targetButton && (
                <Button
                    className={styles.button}
                    onClick={() => {
                        if (notification?.action?.targetButton)
                            scrollToTarget(notification?.action?.targetButton.target);
                        closeNotification();
                    }}
                    variant="clean"
                >
                    {notification.action.targetButton.text}
                </Button>
            )}
            <div style={{ "--or-progress-timer": timer }} className={styles["notification-timer-bar"]}>
                <div className={styles["notification-timer"]} />
            </div>
        </div>
    );
};

export default Notification;
